import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { surfaceTokens } from "../../config/theme";

interface ExposureBarProps {
  /** Exposure as a 0–100 percentage. */
  value: number;
  /** Optional reference tick (e.g. 8.3% expected ownership in a 12-team draft). */
  reference?: number;
  /**
   * Portfolio-average exposure. When set, the bar renders in DIVERGING mode:
   * a center line at the average, over-exposure extends right (brand), under
   * extends left (dim) — so the delta vs your own behavior is the shape. The
   * fixed `expected` tick (market-neutral) still shows for context.
   */
  center?: number;
  /** Market-neutral expected share, shown as a fixed tick in diverging mode. */
  expected?: number;
  /** Bar fill color. Defaults to brand primary. */
  color?: string;
  /** Track width; a number (px) or any CSS width (e.g. "100%"). */
  width?: number | string;
  /** Whether to show the numeric label to the right of the bar. */
  showLabel?: boolean;
}

// % delta from center → % of a half-track (so a ~30% swing fills a full half).
const DIVERGE_SCALE = 1.55;

/**
 * Horizontal exposure micro-bar. Default mode fills from the left; when a
 * `center` (portfolio average) is given it diverges around that center.
 * Single source for the previously-duplicated exposure bars in tables/cards.
 */
export default function ExposureBar({
  value,
  reference,
  center,
  expected = 100 / 12,
  color,
  width = 48,
  showLabel = true,
}: ExposureBarProps) {
  const { palette } = useTheme();
  const c = surfaceTokens(palette.mode === "dark");
  // brandLight reads clearly on both surfaces (greenOnDark #6db85b in dark).
  const fill = color ?? c.brandLight;
  const pct = Number.isFinite(value) ? Math.max(0, Math.min(value, 100)) : 0;
  const diverging = center != null && Number.isFinite(center);

  const delta = diverging ? pct - (center as number) : 0;
  const over = delta >= 0;
  const halfW = Math.min(Math.abs(delta) * DIVERGE_SCALE, 48);
  const expectedPos = Math.max(1, Math.min(99, 50 + (expected - (center ?? 0)) * DIVERGE_SCALE));

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          position: "relative",
          width,
          height: 8,
          bgcolor: c.surface2,
          boxShadow: `inset 0 0 0 1px ${c.borderLight}`,
          borderRadius: "4px",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {diverging ? (
          <>
            {/* over/under fill diverging from the center */}
            <Box
              sx={{
                position: "absolute", top: 0, bottom: 0, borderRadius: "3px",
                bgcolor: over ? fill : c.dim,
                opacity: over ? 1 : 0.8,
                width: `${halfW}%`,
                ...(over ? { left: "50%" } : { right: "50%" }),
                transition: "width 0.3s ease",
              }}
            />
            {/* portfolio-average center line */}
            <Box sx={{ position: "absolute", left: "50%", top: -2, bottom: -2, width: "1.5px", bgcolor: c.textLight, transform: "translateX(-50%)" }} />
            {/* market-neutral expected tick */}
            <Box sx={{ position: "absolute", left: `${expectedPos}%`, top: -1, bottom: -1, width: "1px", bgcolor: c.dim }} title={`expected ${expected.toFixed(1)}%`} />
          </>
        ) : (
          <>
            <Box
              sx={{
                position: "absolute", left: 0, top: 0, height: "100%",
                width: `${pct}%`, bgcolor: fill, borderRadius: "3px",
                transition: "width 0.3s ease",
              }}
            />
            {reference !== undefined && (
              <Box sx={{ position: "absolute", top: -1, bottom: -1, left: `${Math.max(0, Math.min(reference, 100))}%`, width: "1.5px", bgcolor: c.dim }} />
            )}
          </>
        )}
      </Box>
      {showLabel && (
        <Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 12 }}>
          {pct.toFixed(pct % 1 === 0 ? 0 : 1)}%
        </Typography>
      )}
    </Box>
  );
}
